<template>
  <section
    :id="authors.id"
    class="content-visibility relative sm:mx-auto pl-4 sm:px-8 py-6 rounded-none sm:rounded-lg"
    :class="isMobile ? 'h-min-[350px]' : 'h-min-[345px]'"
    :style="{ backgroundColor: constantData.style.containerColor }"
  >
    <div class="widget-header w-full relative flex items-center mb-8">
      <div
        class="flex items-center relative w-auto"
        :style="{ backgroundColor: constantData.style.containerColor, color: constantData.style.headerTextColor }"
      >
        <OIcon :icon="`category/${constantData.style.icon}-white`" svg-class="w-7 h-7" />
        <div class="block pl-1 flex-shrink-0 mr-1" :class="isMobile ? 'text-xl' : 'text-20'">{{ constantData.text.headerName }}</div>
      </div>
      <a
        :href="constantData.text.headerLink"
        class="absolute flex items-center"
        :class="isMobile ? 'right-3' : 'right-5'"
        :style="{ backgroundColor: constantData.style.containerColor, color: constantData.style.linkColor }"
      >
        <div class="pl-1 flex-shrink-0 font-normal" :class="isMobile ? 'text-md' : 'text-base'">{{ constantData.text.headerLinkName }}</div>
        <ORawIcon icon="homepage/arrow-circle-right" svg-class="w-4 h-4 mx-1" />
      </a>
    </div>
    <Swiper :settings="settings" :modules="[Navigation]" @swiper="onSwiper">
      <SwiperSlide v-for="(author, index) in authors.data" :key="index">
        <OLink :href="`/yazio/${author.slug}`" class="yazio-wrapper">
          <div class="flex text-center items-center justify-start flex-col text-13 relative">
            <div class="yazio-image-wrapper h-[120px] w-[120px] sm:h-[135px] sm:w-[135px] block mb-1">
              <OImage
                loading="lazy"
                :src="author.image"
                class="yazio-image absolute h-[117px] w-[117px] sm:h-[132px] sm:w-[132px] rounded-full object-cover p-2"
              />
            </div>
            <div class="font-semibold text-base">{{ author.name }}</div>
            <div class="text-13">{{ author.title }}</div>
          </div>
        </OLink>
      </SwiperSlide>
    </Swiper>
    <ORawIcon
      v-if="!isMobile"
      ref="prevNavBtn"
      icon="homepage/arrow-circle-right"
      :style="{ color: constantData.style.arrowColor }"
      svg-class="
        absolute
        left-0
        top-1/2
        -translate-y-1/2 -translate-x-1/2
        u-center
        w-12
        h-12
        rounded-full
        z-1
        cursor-pointer
        border-white border-3
        bg-white
        rotate-180
        text-xl"
    />
    <ORawIcon
      v-if="!isMobile"
      ref="nextNavBtn"
      icon="homepage/arrow-circle-right"
      :style="{ color: constantData.style.arrowColor }"
      svg-class="
        absolute
        right-0
        top-1/2
        -translate-y-1/2
        translate-x-1/2
        u-center
        cursor-pointer
        w-12
        h-12
        rounded-full
        border-white border-3
        bg-white
        z-1
        text-xl"
    />
  </section>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from '@nuxtjs/composition-api';
import { Navigation } from 'swiper';
import Swiper from '@/modules/sliders/core/Swiper';
import SwiperSlide from '@/modules/sliders/core/SwiperSlide';
import { useTracking } from '@/modules/tracking/composables/useTracking';
import { useDevice } from '~/composables/useDevice';
import { onedioEvents } from '~/modules/tracking/scripts/onedioEvents';
import { OLink, OIcon, OImage, ORawIcon } from '~/modules/common/components/ui';

export default defineComponent({
  components: { Swiper, SwiperSlide, OLink, OIcon, OImage, ORawIcon },
  props: {
    authors: { type: Object },
  },

  setup(props) {
    const swiperRef = ref(null);
    const nextNavBtn = ref(null);
    const prevNavBtn = ref(null);
    const { sendWidgetEvent } = useTracking();
    const { sendHomepageEvent } = onedioEvents();
    const impressionTimeout = ref(null);
    onMounted(() => {
      for (let i = 0; i < (isMobile.value ? 2 : 6); i++) {
        sendWidgetEvent('view', 'Yazio', props.authors.data[i].name, i + 1);
      }
    });
    const onSwiper = (swiper) => {
      swiperRef.value = swiper;
      swiper.on('slideChange', function () {
        clearTimeout(impressionTimeout.value);
        impressionTimeout.value = setTimeout(() => {
          const slideIndex = swiper.realIndex + (isMobile.value ? 1 : 0);
          for (let i = slideIndex; i < (isMobile.value ? slideIndex + 1 : slideIndex + 6); i++) {
            sendWidgetEvent('view', 'Yazio', props.authors.data[i].name, i + 1);
          }
        }, 1000);
      });
      swiper.on('click', function () {
        const slideIndex = parseInt(swiper.clickedSlide.dataset.swiperSlideIndex);
        sendHomepageEvent('Yazio', 'click', props.authors.data[slideIndex].name);
      });
    };
    const slideChange = (index) => {
      swiperRef.value?.slideToLoop(index);
    };
    // Temporary data. When data will come from backend, these constants will be changed
    const constantData = {
      style: {
        icon: 'yazio',
        containerColor: '#9786BF',
        arrowColor: '#6F59A1',
        linkColor: '#FFFFFF',
        headerTextColor: '#FFFFFF',
      },
      text: {
        headerName: 'Yazio',
        headerLinkName: 'Tümünü Gör',
        headerLink: '/yazio',
      },
    };

    const { isMobile } = useDevice();
    const settings = computed(() => ({
      slidesPerView: isMobile.value ? 2.4 : 6,
      slidesPerGroup: isMobile.value ? 1 : 6,
      speed: 150,
      loop: true,
      preloadImages: false,
      navigation: {
        nextEl: nextNavBtn,
        prevEl: prevNavBtn,
      },
      breakpoints: {
        320: { spaceBetween: 16 },
        640: { spaceBetween: 24 },
      },
    }));
    return {
      settings,
      nextNavBtn,
      prevNavBtn,
      onSwiper,
      constantData,
      slideChange,
      Navigation,
      isMobile,
    };
  },
});
</script>

<style scoped lang="scss">
.yazio-wrapper {
  display: block;
  color: #ffffff;
  transition: all 0.2s 0s ease-in;
  &:hover {
    transform: scale(0.95);
  }
}
.yazio-image-wrapper {
  position: relative;
  border-radius: 100%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.4192270658263305) 0%, rgba(255, 255, 255, 0) 100%);
}
.yazio-image {
  background-color: #9786bf;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.widget-header::before {
  content: '';
  position: absolute;
  width: 100%;
  top: 50%;
  transform: translate(0, -50%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.22);
}
.content-visibility {
  content-visibility: auto;
  contain-intrinsic-size: 293px;
  @include sm() {
    content-visibility: visible;
    contain-intrinsic-size: 308px;
  }
}
</style>
